import { session } from "../services/session";
import storage from "./storage";
import { toDayCountMap } from "./dayCounts";

// Playback feature RPC callers - kept separate from metricsModal.ts since
// this is a distinct, self-contained surface (see the Playback plan).

export interface PlaybackAction {
  type: "arrival" | "pageview" | "departure";
  t: number;
  session?: any;
  sessionId?: string;
  pathName?: string;
  // True only on the specific pageview action that matches the session's
  // persisted goalUrl - the actual moment of conversion within this
  // session's replay, not a session-wide flag (see getPlaybackTimeline's
  // own comment on the backend for why that distinction matters).
  goalReached?: boolean;
}

export interface PlaybackTimeline {
  date: string;
  actions: PlaybackAction[];
  // EVERY session for the day, ground truth - not just the ones included in
  // `actions`. Each carries `includedInPlayback` so the Sessions-tab drawer
  // can show every real session while tagging the ones the bubble animation
  // had to thin out during a crowded moment (see getPlaybackTimeline's own
  // comment on the backend for the concurrency-based inclusion decision).
  // Sorted by arrival, same ordering `actions` is built from, so the
  // frontend can reveal both in lockstep with the same virtual clock.
  allSessions: any[];
  sessionCount: number;
  // Sessions actually included in `actions` - differs from sessionCount when
  // the backend sampled a very busy day down to a representative subset.
  playedSessionCount?: number;
  sampled?: boolean;
  truncated: boolean;
  // True when the requested date is older than the account's real
  // storageDays retention window - the backend already computed this
  // (getPlaybackTimeline checks it explicitly since playback bypasses the
  // usual formPeriodParams floor), but until now nothing in the frontend
  // ever read it, so an out-of-range day just silently rendered empty with
  // zero explanation instead of surfacing earliestAccessibleDate below.
  restricted?: boolean;
  // WHY it was refused. The backend distinguishes three cases and the UI must
  // too - see PlaybackControlBar's badge. Collapsing them tells a customer to
  // "pick a different date" when no date would ever work, or blames retention
  // for a plan limit.
  restrictedReason?: "plan" | "retention" | "playback_window";
  earliestAccessibleDate?: string;
}

export interface PlaybackDayMetrics {
  date: string;
  hourly: { hour: number; visitorCount: number }[];
  summary: {
    totalSession: number;
    bounceRate: string;
    avgSessionDuration: number;
    newUsers: number;
    pageviewsPerSession: number;
  };
  restricted?: boolean;
  earliestAccessibleDate?: string;
}

// "date" alone is ambiguous about WHICH midnight it means - the backend used
// to always assume UTC midnight, which silently shifted the whole day's
// window (and therefore "now," for a same-day playback) by the viewer's own
// UTC offset in either direction. Sending the viewer's own local midnight as
// an absolute instant removes that ambiguity entirely: the backend uses
// exactly this moment as day-start, so pacing/effectiveDayMs/display all end
// up anchored to the SAME reference throughout, with no reinterpretation
// needed anywhere.
const localDayStartMs = (date: string): number => {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0).getTime();
};

export const getPlaybackTimeline = async (
  date: string
): Promise<PlaybackTimeline> => {
  const response: any = await session({
    jsonprc: "2.0",
    method: "getPlaybackTimeline",
    params: {
      siteId: storage.get("siteId"),
      date,
      localDayStartMs: localDayStartMs(date),
    },
    id: 0,
  });

  return response?.data?.result;
};

export const getPlaybackDayMetrics = async (
  date: string
): Promise<PlaybackDayMetrics> => {
  const response: any = await session({
    jsonprc: "2.0",
    method: "getPlaybackDayMetrics",
    params: {
      siteId: storage.get("siteId"),
      date,
      localDayStartMs: localDayStartMs(date),
    },
    id: 0,
  });

  return response?.data?.result;
};

// Page-slot "conversions happened here" badge for playback - a pure
// historical record for this one day (derived backend-side from the
// persisted per-session goalUrl, not today's live goal config - see
// MetricsRepository.getConvertedUrlsForDay's own comment). Returns the
// distinct set of URLs actually converted on this day; clusterService.ts
// just checks each page slot's own URL for membership.
export const getPlaybackConvertedUrls = async (
  date: string
): Promise<string[]> => {
  const response: any = await session({
    jsonprc: "2.0",
    method: "getPlaybackConvertedUrls",
    params: {
      siteId: storage.get("siteId"),
      date,
      localDayStartMs: localDayStartMs(date),
    },
    id: 0,
  });

  return response?.data?.result || [];
};

// Date picker's "which days have any data" hint - PlaybackControlBar.tsx
// calls this once per visible month (on open, and again whenever the
// calendar navigates to a different month via onShownDateChange). Returns
// distinct 'YYYY-MM-DD' strings for days with any session activity in the
// given range; the calendar just checks each rendered day for membership.
export const getPlaybackDaysWithData = async (
  rangeStart: Date,
  rangeEnd: Date
): Promise<string[]> => {
  const response: any = await session({
    jsonprc: "2.0",
    method: "getPlaybackDaysWithData",
    params: {
      siteId: storage.get("siteId"),
      rangeStartMs: rangeStart.getTime(),
      rangeEndMs: rangeEnd.getTime(),
      // Without this the backend fell back to grouping by UTC calendar
      // day, which mislabeled sessions in the last few UTC hours of a day
      // as belonging to the NEXT day for any viewer west of UTC - the dot
      // would show on a day that then loaded empty when clicked, since the
      // actual playback fetch buckets by local-midnight boundaries
      // instead. Sending the viewer's own IANA zone lets both queries
      // agree on where each day starts.
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    id: 0,
  });

  return response?.data?.result || [];
};

// How many sessions each day of the range has, for the date picker's per-day marks. null when the server could not say (an older backend that does not know the method, or any
// failure): the picker then falls back to the yes/no underline from getPlaybackDaysWithData above, never to a blank calendar.
export const getPlaybackDayCounts = async (rangeStart: Date, rangeEnd: Date): Promise<Record<string, number> | null> => {
  const response: any = await session({
    jsonprc: "2.0",
    method: "getPlaybackDayCounts",
    params: {
      siteId: storage.get("siteId"),
      rangeStartMs: rangeStart.getTime(),
      rangeEndMs: rangeEnd.getTime(),
      // the viewer's own zone, for the same reason as getPlaybackDaysWithData: both queries must agree on where a day starts
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    id: 0,
  });
  const result = response?.data?.result;

  return Array.isArray(result) ? toDayCountMap(result) : null;
};

/*
 * Which empty-state copy to show in playback (2026-08-27).
 *
 * The empty state used to say "no sessions this day / try picking a different
 * date" even when the day had been REFUSED rather than being empty - wrong on
 * both counts, since the data may exist and no other date would help.
 *
 * It now says only that the day cannot be shown. The REASON, and the upgrade
 * path, live in PlanLimitBanner at the top of the app, because that is a
 * system-level fact about the account rather than a fact about this panel -
 * and because putting the upsell here as well meant three notices for one
 * condition.
 */
export const playbackEmptyStateKeys = (
  restrictedReason: string | null
): { title: string; desc: string } => {
  if (restrictedReason) {
    return {
      title: "playback_restricted_generic_title",
      desc: "playback_restricted_generic_desc",
    };
  }

  return {
    title: "no_sessions_this_day",
    desc: "no_recorded_sessions_for_this_day",
  };
};
